import {
  METRICS_CUSTOM_COLUMN,
  METRICS_CUSTOM_EVENT,
  METRICS_CUSTOM_EVENT_HIDDEN_REVENUE,
  METRICS_CUSTOM_EVENT_REVENUE,
} from '@/services/constants/report';

export type MetricColumnDraft = {
  text: string;
  value: string;
  isCustomColumn?: boolean;
  disabled?: boolean;
  selected?: boolean;
  isTreeLevel?: boolean;
};

export const buildTreeLevelMetricColumns = (options: {
  treeLevels: string[] | undefined;
  dimensions: Record<string, { mainColumn: string }>;
  reportMetricsName: (key: string) => string;
}): MetricColumnDraft[] => {
  return options.treeLevels
    ?.filter((item) => !!item)
    ?.map((item) => {
      const name = options.dimensions[item].mainColumn;
      return {
        text: options.reportMetricsName(name),
        value: name,
      };
    }) || [];
};

export const buildDimensionMetricDrafts = (options: {
  additionalColumns: string[];
  metrics: string[];
}): MetricColumnDraft[] => {
  return options.additionalColumns.concat(options.metrics).map((name) => {
    return {
      text: name,
      value: name,
      isCustomColumn: name.startsWith(METRICS_CUSTOM_COLUMN),
    };
  });
};

export const filterMetricDraftsByReportExclude = (
  drafts: MetricColumnDraft[],
  type: string,
  reportExclude: Record<string, string[]> | undefined,
) => {
  const exclude = reportExclude?.[type];
  if (!exclude) {
    return drafts;
  }

  return drafts.filter((item) => !exclude.includes(item.value));
};

export const buildLockedMainColumnDraft = (mainColumn: string): MetricColumnDraft => {
  return {
    text: mainColumn,
    value: mainColumn,
    disabled: true,
    selected: true,
  };
};

export type MetricSaveFlag = {
  value: string;
  disabled?: boolean;
  isTreeLevel?: boolean;
};

export const listSimpleMetricSaveFlags = (options: {
  additionalColumns: string[];
  metrics: string[];
  mainColumn: string;
  treeLevelColumns: string[];
  exclude?: string[];
}): MetricSaveFlag[] => {
  const exclude = new Set(options.exclude || []);
  const columns = options.additionalColumns
    .concat(options.metrics)
    .filter((value) => !exclude.has(value))
    .map((value) => ({ value }));

  return [
    { value: options.mainColumn, disabled: true },
    ...options.treeLevelColumns.map((value) => ({
      value,
      disabled: true,
      isTreeLevel: true,
    })),
    ...columns,
  ];
};

export const listDedicatedMetricSaveFlags = (options: {
  availableMetrics: string[];
  defaultInclude: string[];
}): MetricSaveFlag[] => {
  const locked = new Set(options.defaultInclude);

  return options.availableMetrics.map((value) => ({
    value,
    ...(locked.has(value) ? { disabled: true } : {}),
  }));
};

export const buildTreeLevelGroupingDrafts = (treeLevelColumns: MetricColumnDraft[]): MetricColumnDraft[] => {
  return treeLevelColumns.map((item) => ({
    ...item,
    disabled: true,
    selected: true,
    isTreeLevel: true,
  }));
};

export const getCustomColumnParams = (
  name: string,
  getCustomColumns: () => Record<string, {
    id?: string;
    formula?: string;
    name?: string;
    decimalPlaces?: number;
    format?: string;
    description?: string;
  }>,
) => {
  if (!name.startsWith(METRICS_CUSTOM_COLUMN)) {
    return undefined;
  }

  const columns = getCustomColumns()[name.slice(METRICS_CUSTOM_COLUMN.length)] || {};
  const { id, formula, name: text = '', decimalPlaces, format, description = '' } = columns;

  return {
    id,
    formula,
    text,
    decimalPlaces,
    format,
    description,
  };
};

export const applyMetricColumnHelp = <T extends { value: string }>(options: {
  columns: T[];
  existedColumns?: boolean;
  resolveIndex: (value: string) => number;
  resolveShortHelp: (value: string) => string | null;
  resolveFullHelp: (value: string) => string | null;
  groupingValueList: string[];
  getCustomColumnParams: (value: string) => Record<string, unknown> | undefined;
}) => {
  if (!options.columns) {
    return options.columns;
  }

  return options.columns.map((item, index) => {
    const resultIndex = options.existedColumns === false
      ? options.resolveIndex(item.value)
      : index;

    return Object.assign(item, {
      help: options.resolveShortHelp(item.value),
      helpFull: options.resolveFullHelp(item.value),
      index: resultIndex,
      export: options.groupingValueList.includes(item.value),
      ...options.getCustomColumnParams(item.value),
    });
  });
};

export const isFormulaMetricValue = (value: string) => {
  return value.startsWith(METRICS_CUSTOM_COLUMN)
    || value.startsWith(METRICS_CUSTOM_EVENT)
    || value.startsWith(METRICS_CUSTOM_EVENT_REVENUE)
    || value.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE);
};

export const filterDefaultSelectedMetrics = <T extends { value: string }>(
  items: T[],
  options: {
    defaultInclude: string[];
    defaultOmit: string[];
  },
) => {
  return items.filter((item) => {
    if (isFormulaMetricValue(item.value)) {
      return false;
    }

    if (options.defaultInclude.includes(item.value)) {
      return true;
    }

    return !options.defaultOmit.includes(item.value);
  });
};

export type DedicatedMetricColumn = MetricColumnDraft & {
  index: number;
  help: string | null;
  helpFull: string | null;
};

export const buildDedicatedMetricColumns = (options: {
  availableMetrics: string[];
  defaultInclude: string[];
  reportMetricsName: (name: string) => string;
  getShortHelp: (name: string) => string | null;
  getFullHelp: (name: string) => string | null;
}): DedicatedMetricColumn[] => {
  return options.availableMetrics.map((name, index) => {
    const column: DedicatedMetricColumn = {
      text: options.reportMetricsName(name),
      value: name,
      index,
      help: options.getShortHelp(name),
      helpFull: options.getFullHelp(name),
    };

    if (options.defaultInclude.includes(name)) {
      column.disabled = true;
      column.selected = true;
    }

    return column;
  });
};

export const buildExistingMetricsForRoute = (
  routeName: string | symbol | null | undefined,
  type: string,
  options: {
    isDedicatedRoute: (routeName: string | symbol | null | undefined) => boolean;
    getDedicatedColumns: (routeName: string | symbol | null | undefined) => DedicatedMetricColumn[];
    getSimpleColumns: (type: string) => DedicatedMetricColumn[];
  },
) => {
  if (options.isDedicatedRoute(routeName)) {
    return options.getDedicatedColumns(routeName);
  }

  return options.getSimpleColumns(type);
};
